import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import {
  DuplicateProcessRequest,
  DuplicateProcessRequestSchema,
  ProcessListRow
} from '@bliks-io/contracts';
import { useDuplicateProcess } from '@/api/mutations/useProcesses';
import { Input, Label, cn } from '@bliks-io/ui';
import { FormDialog } from '@/components/dialogs/FormDialog';

interface DuplicateProcessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: ProcessListRow;
}

export function DuplicateProcessDialog({
  open,
  onOpenChange,
  source
}: DuplicateProcessDialogProps) {
  const { t } = useTranslation();
  const duplicateProcess = useDuplicateProcess();

  const defaultValues: DuplicateProcessRequest = {
    name: t('processes.duplicateNameDefault', { name: source.name })
  };

  const onSubmit = async (data: DuplicateProcessRequest) => {
    await duplicateProcess.mutateAsync({ sourceId: source._id, data });
    toast.success(t('processes.duplicateSuccess'));
    onOpenChange(false);
  };

  return (
    <FormDialog<DuplicateProcessRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={t('processes.duplicateTitle')}
      description={t('processes.duplicateDescription', { name: source.name })}
      icon={Copy}
      schema={DuplicateProcessRequestSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitLabel={t('processes.duplicateTitle')}
      cancelLabel={t('common.cancel')}
      size="md"
    >
      {({ form }) => (
        <div className="space-y-2">
          <Label htmlFor="name">{t('common.Name')}</Label>
          <Input
            id="name"
            type="text"
            {...form.register('name')}
            className={cn(form.formState.errors.name && 'border-destructive')}
          />
          {form.formState.errors.name?.message && (
            <p className="text-sm text-destructive">{t(form.formState.errors.name.message)}</p>
          )}
        </div>
      )}
    </FormDialog>
  );
}
